import {useEffect, RefObject, useLayoutEffect} from 'react';

type Options = {
  customDocument?: Document;
  customWindow?: Window;
};
export const useOnEscape = (
  handler: (event: KeyboardEvent) => void,
  active = true,
  options: Options,
) => {
  const doc = options?.customDocument || document;

  useEffect(() => {
    if (!active) return;
    const listener = (event: KeyboardEvent) => {
      // check if key is an Escape
      if (event.key === 'Escape') handler(event);
    };
    doc?.addEventListener('keyup', listener);

    return () => {
      if (!active) return;
      doc?.removeEventListener('keyup', listener);
    };
  }, [handler, active, doc]);
};

export const useOnClickOutside = (
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[],
  handler: (event: TouchEvent | MouseEvent) => void,
  active = true,
  options: Options,
) => {
  const doc = options?.customDocument || document;

  useEffect(() => {
    if (!active) return;
    const listener = (event: TouchEvent | MouseEvent) => {
      // Do nothing if clicking ref's element or descendent elements
      const refs = Array.isArray(ref) ? ref : [ref];

      let contains = false;
      refs.forEach(r => {
        if (!r.current || r.current.contains(event.target as Node)) {
          contains = true;
          return;
        }
      });
      event.stopPropagation();
      if (!contains) handler(event);
    };

    doc?.addEventListener('mousedown', listener);
    doc?.addEventListener('touchstart', listener);

    return () => {
      if (!active) return;
      doc?.removeEventListener('mousedown', listener);
      doc?.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, active, doc]);
};

// Make sure that user is not able TAB out of the Modal content on Open
export const useTabbing = (
  contentRef: RefObject<HTMLElement>,
  active = true,
  options: Options,
) => {
  const doc = options?.customDocument || document;

  useEffect(() => {
    if (!active) return;
    const listener = (event: KeyboardEvent) => {
      // check if key is an Tab
      if (event.keyCode === 9) {
        const els = contentRef?.current?.querySelectorAll(
          'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]',
        );

        const focusableEls = Array.prototype.slice.call(els);
        if (focusableEls.length === 1) {
          event.preventDefault();
          return;
        }

        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];
        if (event.shiftKey && doc?.activeElement === firstFocusableEl) {
          event.preventDefault();
          lastFocusableEl.focus();
        } else if (doc?.activeElement === lastFocusableEl) {
          event.preventDefault();
          firstFocusableEl.focus();
        }
      }
    };

    doc?.addEventListener('keydown', listener);

    return () => {
      if (!active) return;
      doc?.removeEventListener('keydown', listener);
    };
  }, [contentRef, active, doc]);
};

export const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;
